import { useCallback, useEffect, useMemo } from 'react';

import { DIM_OPACITY_FACTOR } from '../../constants';
import type { BarSeriesProps } from '../../types/BarSeriesProps';
import type { ChartDataPoint, ChartDataPointValue } from '../../types/ChartDataPoint';
import { useChartContext } from '../ChartContext';
import { Bars } from '../private/Bars';
import { BaseBarSeries } from '../private/BaseBarSeries';

/**
 * BarSeries component for composable ColumnChart.
 *
 * Wrapper around visx's BarSeries.
 * Handles data transformation and auto-coloring based on series order.
 *
 * @example
 * ```tsx
 * <ColumnChart>
 *   <BarSeries
 *     seriesKey="latency"
 *     name="API Latency"
 *     data={data}
 *     dataKey="latency"
 *     color="#4d77ff"
 *   />
 * </ColumnChart>
 * ```
 */
export function BarSeries({
  seriesKey,
  name,
  dataKey,
  color,
  xAccessor,
  yAccessor,
  tooltipRowIcon,
  showInTooltip = true,
  stacked = true,
  dimmed = false,
  ...rest
}: BarSeriesProps) {
  const {
    chartData,
    xDataKey,
    colorScale,
    registerSeriesDisplayMetadata,
    unregisterSeriesDisplayMetadata,
    chartClipPathId,
  } = useChartContext();

  // Determine color (use provided or auto-assign from ordinal scale)
  // The scale automatically assigns colors as new series keys are encountered
  const seriesColor = color || colorScale(seriesKey);

  // Register series metadata for tooltip display
  // Only register if showInTooltip is true (default behavior)
  useEffect(() => {
    if (!showInTooltip) {
      return;
    }

    registerSeriesDisplayMetadata(seriesKey, {
      name,
      color: seriesColor,
      dataKey,
      tooltipRowIcon,
    });

    return () => {
      unregisterSeriesDisplayMetadata(seriesKey);
    };
  }, [
    showInTooltip,
    seriesKey,
    name,
    seriesColor,
    dataKey,
    tooltipRowIcon,
    registerSeriesDisplayMetadata,
    unregisterSeriesDisplayMetadata,
  ]);

  // Helper to access x-value from data point using either string or symbol key
  const getXValue = useCallback(
    (d: ChartDataPoint | undefined): ChartDataPointValue => {
      if (!d) {
        return null;
      }
      return d[xDataKey] ?? null;
    },
    [xDataKey],
  );

  // Build accessors (use provided or build defaults)
  // Note: visx may pass undefined in some edge cases (e.g., when computing nearest datum across series with different x-values)
  // We provide defensive checks to prevent crashes
  const defaultXAccessor = useCallback(
    (d: ChartDataPoint | undefined) => {
      return getXValue(d) ?? 0;
    },
    [getXValue],
  );
  const defaultYAccessor = useCallback(
    (d: ChartDataPoint | undefined) => {
      if (!d) {
        return null;
      }
      const value = d[dataKey];
      // Return null for null/undefined values so visx skips those points
      return value ?? null;
    },
    [dataKey],
  );

  // For band scales, convert xAccessor to return strings that match the domain
  // Key difference from LineChart: LineChart uses numeric domain [min, max]
  // ColumnChart uses string[] domain, so xAccessor must ALWAYS return strings to match domain categories
  const bandScaleXAccessor = useMemo(() => {
    // For band scales, ALWAYS convert to strings to match string domain

    return (d: ChartDataPoint | undefined) => {
      const xValue = xAccessor ? xAccessor(d) : defaultXAccessor(d);
      return String(xValue);
    };
  }, [xAccessor, defaultXAccessor]);

  // Use all chart data - don't filter out null values
  // This ensures the x-axis domain includes all timestamps (even where this series has no data)
  // visx will skip rendering bars where yAccessor returns null
  const filteredData = useMemo(() => {
    // Return empty array if no data
    if (!chartData) {
      return [];
    }

    return chartData;
  }, [chartData]);

  return (
    <BaseBarSeries
      BarsComponent={Bars}
      dataKey={seriesKey}
      data={filteredData}
      xAccessor={bandScaleXAccessor}
      yAccessor={yAccessor ?? defaultYAccessor}
      colorAccessor={() => seriesColor}
      stacked={stacked}
      opacity={dimmed ? DIM_OPACITY_FACTOR : 1}
      clipPath={`url(#${chartClipPathId})`}
      {...rest}
    />
  );
}

// displayName for identifying BarSeries in children extraction (supports React.memo)
BarSeries.displayName = 'BarSeries';
